// stale-cart-line-cleanup.js
//
// Silently removes cart lines that Shopify has set to quantity 0, so one
// unsellable item cannot block every later add to the cart.
//
// What happens without this:
//   1. MediaClip adds a project as a pair: a priced print line plus the zero-
//      priced "Your Print Order" reference line (PRINT-ORDER-001).
//   2. If Shopify cannot sell the print variant to this buyer it keeps the
//      line at quantity 0, returns MERCHANDISE_OUT_OF_STOCK, and MediaClip
//      shows its "Product Out Of Stock" page.
//   3. That quantity-0 line stays in the cart, and Shopify repeats the same
//      warning on EVERY later cart mutation. MediaClip treats any warning as
//      a failed add, so every later add shows "out of stock" — even though the
//      new line was actually added.
//
// A quantity-0 line can't be bought and is never charged, so removing it loses
// nothing. Items that are in the cart but can't be delivered to the shopper's
// country are left alone: checkout already validates the shipping region.
//
// The quantity-0 line is invisible to the Ajax cart (/cart.js), so it can only
// be removed through the Storefront API (cartLinesRemove), which accepts
// tokenless cart calls. It goes together with every other line of the same
// MediaClip project (the reference line and any add-page lines), in one call.
//
// Shopify only re-checks whether each line can still be sold when the cart is
// changed. After a region switch an unsellable line therefore still shows
// quantity 1 until the next cart change — which would be MediaClip's add,
// failing with "out of stock". So after a region switch the cleanup first makes
// a no-op change (every line set to the quantity it already has); Shopify
// re-checks the cart for the current country and zeroes what can't be sold, and
// that is then removed. Without a region switch nothing needs re-checking (a
// failed add has already left its line at quantity 0), so ordinary cart and My
// Projects visits only read the cart and never write to it. The first cleanup
// page in a browser also re-checks once (staleCartRecheckDone), in case the
// shopper switched region before this shipped; the country itself is recorded
// on every page, so a later switch from any page is still detected.
//
// When to run: on the cart, My Projects and designer pages, and on ANY page
// right after the shop country changed (region switch), so the project is gone
// before the shopper reaches the cart. Afterwards the display is brought up to
// date: on the cart page (server-rendered before this runs) the theme's own
// <cart-items> refresh redraws the rows, subtotal and badge in place — no
// reload, so a region switch on the cart page is one visible change. Only if
// the cart ends up empty does the page reload, to show the empty-cart state.
// Other pages refresh the header cart badge. Right after a region switch the
// cart page shows "Updating your cart..." over the cart contents
// (data-stale-cart-updating, set in sections/main-cart-items.liquid) until
// this has finished, so a half-removed project is never visible.
//
// Note: the MediaClip designer itself (/tools/customize) is rendered by the
// MediaClip app without this theme's layout, so this script does not run
// there. It does run on MediaClip's "Product Out Of Stock" page
// (/tools/customize/product-out-of-stock), which uses the theme layout — that
// is where a failed add's leftover lines are cleared.
(function () {
  'use strict';

  // Keep on a supported version (Shopify supports each for about a year).
  var STOREFRONT_API_VERSION = '2026-07';
  var FETCH_TIMEOUT_MS = 8000;

  var nativeFetch =
    typeof window.fetch === 'function'
      ? window.fetch.bind(window)
      : function () {
          return Promise.reject(new Error('fetch unavailable'));
        };

  var LINE_FIELDS =
    'lines(first: 250) { nodes { id quantity attributes { key value } ' +
    'merchandise { ... on ProductVariant { price { amount } product { title } } } } }';

  var LINES_QUERY = 'query StaleCartLines($id: ID!) { cart(id: $id) { ' + LINE_FIELDS + ' } }';

  // Same read in the shopper's language and country, so product names in the
  // cart message match the translated storefront (e.g. "Standardabzüge" on
  // de-de). Only this read takes a context; the cart itself is not changed.
  var LOCALIZED_LINES_QUERY =
    'query StaleCartLinesLocalized($id: ID!, $language: LanguageCode, $country: CountryCode) ' +
    '@inContext(language: $language, country: $country) { cart(id: $id) { ' + LINE_FIELDS + ' } }';

  // Shopify.locale ("de", "pt-BR", "zh-CN") as a Storefront LanguageCode.
  function storefrontLanguage() {
    var locale = String((window.Shopify && window.Shopify.locale) || '').trim();
    return locale ? locale.toUpperCase().replace('-', '_') : '';
  }

  // Reads the cart lines, localized when possible. If the localized read is
  // rejected (e.g. a language code the API doesn't know) the plain read is
  // used, so names can fall back to the default language but cleanup never
  // stops because of it.
  function readLines(cartId) {
    var language = storefrontLanguage();
    var country = currentCountry();
    var plain = function () {
      return storefront(LINES_QUERY, { id: cartId });
    };
    if (!language || !country) return plain();
    return storefront(LOCALIZED_LINES_QUERY, { id: cartId, language: language, country: country }).then(
      function (result) {
        return result && result.data && result.data.cart ? result : plain();
      },
      plain
    );
  }

  // Product names from the localized read, by line id (the re-check's own
  // result is not localized).
  function withLocalizedTitles(nodes, readNodes) {
    var titles = Object.create(null);
    (readNodes || []).forEach(function (line) {
      var product = line && line.merchandise && line.merchandise.product;
      if (line && line.id && product && product.title) titles[line.id] = product.title;
    });
    return (nodes || []).map(function (line) {
      if (!line || !titles[line.id] || !line.merchandise) return line;
      var merchandise = Object.assign({}, line.merchandise, {
        product: Object.assign({}, line.merchandise.product, { title: titles[line.id] }),
      });
      return Object.assign({}, line, { merchandise: merchandise });
    });
  }

  var TOUCH_MUTATION =
    'mutation TouchCartLines($id: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $id, lines: $lines) { ' +
    'cart { ' + LINE_FIELDS + ' } userErrors { code message } } }';

  var REMOVE_MUTATION =
    'mutation RemoveStaleCartLines($id: ID!, $ids: [ID!]!) { cartLinesRemove(cartId: $id, lineIds: $ids) { ' +
    'cart { id } userErrors { code message } } }';

  function attribute(line, key) {
    var list = (line && line.attributes) || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].key === key) return String(list[i].value || '');
    }
    return '';
  }

  function projectIdOf(line) {
    return attribute(line, '_projectId') || attribute(line, 'projectId');
  }

  // Pure: given Storefront cart lines, return the ids to remove.
  // A quantity-0 line is always dead. Its MediaClip project siblings go too,
  // otherwise the zero-priced reference line is left behind as an orphan.
  function planStaleLineRemoval(lines) {
    var list = (Array.isArray(lines) ? lines : []).filter(Boolean);
    // No prototype: a line's _projectId ("constructor", "__proto__", ...)
    // must never match a built-in key.
    var deadProjects = Object.create(null);
    var remove = Object.create(null);

    list.forEach(function (line) {
      if (!line || !line.id) return;
      if (Number(line.quantity) === 0) {
        remove[line.id] = true;
        var projectId = projectIdOf(line);
        if (projectId) deadProjects[projectId] = true;
      }
    });

    list.forEach(function (line) {
      if (!line || !line.id || remove[line.id]) return;
      var projectId = projectIdOf(line);
      if (projectId && deadProjects[projectId]) remove[line.id] = true;
    });

    return list
      .filter(function (line) {
        return line && remove[line.id];
      })
      .map(function (line) {
        return line.id;
      });
  }

  // Pure: product names for the cart-page message, split into
  //   unavailable: the lines Shopify zeroed (can't be sold in this country);
  //   others:      the rest of their print project, removed with them because
  //                a print project can't be split.
  // Only priced lines are named (the free "Your Print Order" reference line is
  // left out); cart order, no duplicates.
  function describeRemovedLines(lines, ids) {
    var unavailable = [];
    var others = [];
    (Array.isArray(lines) ? lines : []).forEach(function (line) {
      if (!line || ids.indexOf(line.id) === -1) return;
      var merchandise = line.merchandise || {};
      var price = Number(merchandise.price && merchandise.price.amount);
      var title = merchandise.product && merchandise.product.title;
      if (!title || !(price > 0)) return;
      var list = Number(line.quantity) === 0 ? unavailable : others;
      if (list.indexOf(title) === -1) list.push(title);
    });
    others = others.filter(function (title) {
      return unavailable.indexOf(title) === -1;
    });
    return { unavailable: unavailable, others: others };
  }

  // Whether the last run's re-check itself found lines unavailable (they had
  // a quantity before and 0 after). Those are announced; lines that were
  // already at 0 (a failed add) stay silent.
  var lastZeroedByRecheck = false;

  function zeroedByRecheck(before, after) {
    var hadQuantity = Object.create(null);
    (before || []).forEach(function (line) {
      if (line && line.id && Number(line.quantity) > 0) hadQuantity[line.id] = true;
    });
    return (after || []).some(function (line) {
      return line && hadQuantity[line.id] === true && Number(line.quantity) === 0;
    });
  }

  // Names of what the last run removed, for the cart-page message.
  var lastRemovedNames = { unavailable: [], others: [] };

  function cartJsUrl() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return String(base).replace(/\/+$/, '') + '.js';
  }

  // A timed-out request is also aborted, so a late response can't land after
  // the cart queue has moved on.
  function withTimeout(promise, controller) {
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('timeout'));
      }, FETCH_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).then(
      function (value) {
        clearTimeout(timer);
        return value;
      },
      function (error) {
        clearTimeout(timer);
        throw error;
      }
    );
  }

  function newController() {
    return typeof AbortController === 'function' ? new AbortController() : null;
  }

  // GraphQL errors come back with HTTP 200, so they are checked explicitly.
  function storefront(query, variables) {
    var controller = newController();
    return withTimeout(
      nativeFetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: query, variables: variables }),
        signal: controller ? controller.signal : undefined,
      })
        .then(function (response) {
          if (!response.ok) throw new Error('storefront ' + response.status);
          return response.json();
        })
        .then(function (result) {
          if (result && result.errors && result.errors.length) {
            throw new Error('storefront: ' + (result.errors[0] && result.errors[0].message));
          }
          return result;
        }),
      controller
    );
  }

  function isCleanupPage() {
    var path = window.location.pathname || '';
    var cartPath = String((window.routes && window.routes.cart_url) || '/cart').replace(/\/+$/, '');
    if (path === cartPath || path === cartPath + '/') return true;
    if (path.indexOf('/tools/customize') !== -1) return true;
    if (/\/pages\/my-projects\/?$/.test(path)) return true;
    return false;
  }

  // No-op cart change so Shopify re-checks every line for the current country.
  // Returns the re-checked lines, or the lines as read if the change fails.
  function recheckLines(cartId, nodes) {
    var lines = nodes
      .filter(function (line) {
        return line && line.id && Number(line.quantity) > 0;
      })
      .map(function (line) {
        return { id: line.id, quantity: Number(line.quantity) };
      });
    if (!lines.length) return Promise.resolve(nodes);
    return storefront(TOUCH_MUTATION, { id: cartId, lines: lines }).then(
      function (result) {
        var payload = result && result.data && result.data.cartLinesUpdate;
        var rechecked = payload && payload.cart && payload.cart.lines && payload.cart.lines.nodes;
        if (!rechecked || (payload.userErrors && payload.userErrors.length)) return nodes;
        return rechecked;
      },
      function () {
        return nodes;
      }
    );
  }

  // options.recheck: make the no-op change first (region switch only).
  function removeStaleLines(options) {
    var recheck = !!(options && options.recheck);
    lastZeroedByRecheck = false;
    var controller = newController();
    return withTimeout(
      nativeFetch(cartJsUrl(), {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller ? controller.signal : undefined,
      }).then(function (response) {
        if (!response.ok) throw new Error('cart.js ' + response.status);
        return response.json();
      }),
      controller
    ).then(function (cart) {
      // An empty Ajax cart can still hold a quantity-0 line, so item_count
      // is not a reason to skip. No token means no cart yet.
      if (!cart || !cart.token) return null;
      var cartId = 'gid://shopify/Cart/' + cart.token;
      var readNodes = [];
      return readLines(cartId)
        .then(function (result) {
          var nodes = (result && result.data && result.data.cart && result.data.cart.lines.nodes) || [];
          readNodes = nodes;
          if (!recheck) return nodes;
          return recheckLines(cartId, nodes).then(function (rechecked) {
            lastZeroedByRecheck = zeroedByRecheck(nodes, rechecked);
            return rechecked;
          });
        })
        .then(function (nodes) {
          if (!nodes) return null;
          var ids = planStaleLineRemoval(nodes);
          if (!ids.length) return null;
          return storefront(REMOVE_MUTATION, { id: cartId, ids: ids }).then(function (removed) {
            var payload = removed && removed.data && removed.data.cartLinesRemove;
            if (!payload || (payload.userErrors && payload.userErrors.length)) {
              console.warn('[stale-cart-line-cleanup] remove failed', payload && payload.userErrors);
              return null;
            }
            lastRemovedNames = describeRemovedLines(withLocalizedTitles(nodes, readNodes), ids);
            return ids;
          });
        });
    });
  }

  function cleanStaleCartLines(options) {
    var run =
      typeof window.__mimeoRunCartMutation === 'function'
        ? window.__mimeoRunCartMutation
        : function (callback) {
            return Promise.resolve().then(callback);
          };
    return run(function () {
      return removeStaleLines(options);
    }).catch(function (error) {
      // Never block the page on cleanup.
      console.warn('[stale-cart-line-cleanup] skipped:', error && error.message);
      return null;
    });
  }

  window.StaleCartLineCleanup = {
    planStaleLineRemoval: planStaleLineRemoval,
    describeRemovedLines: describeRemovedLines,
    run: cleanStaleCartLines,
  };

  var COUNTRY_KEY = 'staleCartLastCountry';
  // Set once the first re-check has run in this browser (see the header).
  var RECHECK_DONE_KEY = 'staleCartRecheckDone';
  var NOTICE_KEY = 'staleCartRemovedNotice';

  // Region switch only: tell the shopper what was removed. Shown by
  // sections/main-cart-items.liquid — right away if this is the cart page,
  // otherwise the next time they open it. Failed-add leftovers stay silent:
  // MediaClip's "Product Out Of Stock" page has already told them.
  // The notice carries the country it was written for; the cart page only
  // shows it in that country, and a removal for another country replaces it.
  var announced = null;

  function announceRemoval(names) {
    if (!names || !names.unavailable || !names.unavailable.length) return;
    var pending;
    try {
      pending = JSON.parse(storageGet(NOTICE_KEY, true) || 'null');
    } catch (error) {
      pending = null;
    }
    if (!pending || !Array.isArray(pending.unavailable) || !Array.isArray(pending.others) || pending.country !== country) {
      pending = { country: country, unavailable: [], others: [] };
    }
    names.unavailable.forEach(function (name) {
      if (pending.unavailable.indexOf(name) === -1) pending.unavailable.push(name);
    });
    // A name now known to be unavailable is no longer just "removed with its
    // project", so it must not be listed twice.
    pending.others = pending.others.filter(function (name) {
      return pending.unavailable.indexOf(name) === -1;
    });
    names.others.forEach(function (name) {
      if (pending.others.indexOf(name) === -1 && pending.unavailable.indexOf(name) === -1) pending.others.push(name);
    });
    storageSet(NOTICE_KEY, JSON.stringify(pending), true);
    announced = pending;
    try {
      document.dispatchEvent(new CustomEvent('stale-cart-lines:removed', { detail: pending }));
    } catch (error) {
      /* the cart page also reads the queued names on load */
    }
  }

  var RELOAD_KEY = 'staleCartReloadedAt';

  function currentCountry() {
    var shopify = window.Shopify || {};
    var root = document.documentElement || {};
    return String(shopify.country || (root.dataset && root.dataset.countryCode) || '').toUpperCase();
  }

  function storageGet(key, session) {
    try {
      return window[session ? 'sessionStorage' : 'localStorage'].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value, session) {
    try {
      window[session ? 'sessionStorage' : 'localStorage'].setItem(key, value);
    } catch (error) {
      /* storage unavailable */
    }
  }

  function isCartPage() {
    var path = window.location.pathname || '';
    var cartPath = String((window.routes && window.routes.cart_url) || '/cart').replace(/\/+$/, '');
    return path === cartPath || path === cartPath + '/';
  }

  // Re-render the header cart badge from the server (same approach as
  // assets/my-projects.js syncHeaderCartBubble).
  function refreshCartBadge() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || typeof DOMParser === 'undefined') return Promise.resolve();
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return nativeFetch(cartUrl + '?section_id=cart-icon-bubble', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.getElementById('cart-icon-bubble') || parsed.querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      })
      .catch(function () {});
  }

  function reloadOnce() {
    // Guard against a reload loop if a removal somehow doesn't stick.
    var last = Number(storageGet(RELOAD_KEY, true) || 0);
    if (Date.now() - last > 10000) {
      storageSet(RELOAD_KEY, String(Date.now()), true);
      recordCountry();
      if (recheck) storageSet(RECHECK_DONE_KEY, '1');
      // The cart page may already have shown (and cleared) the notice; queue
      // it again so the reloaded empty cart still explains what happened.
      if (announced) storageSet(NOTICE_KEY, JSON.stringify(announced), true);
      window.location.reload();
      return true;
    }
    return false;
  }

  // Cart page: redraw rows, subtotal and badge in place with the theme's
  // <cart-items> refresh (assets/cart.js onCartUpdate). Reload only when the
  // cart is now empty, or when that refresh isn't available.
  var PENDING_RELOAD = new Promise(function () {}); // keeps the loader up until the reload

  function refreshCartPage() {
    var cartItems = document.querySelector && document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.onCartUpdate !== 'function') {
      if (reloadOnce()) return PENDING_RELOAD;
      return refreshCartBadge();
    }
    return nativeFetch(cartJsUrl(), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (cart && Number(cart.item_count) === 0 && reloadOnce()) return PENDING_RELOAD;
        return cartItems.onCartUpdate();
      })
      .catch(function () {
        return cartItems.onCartUpdate();
      });
  }

  // Bring the page up to date after lines were removed.
  function updateDisplay(removed) {
    if (!removed || !removed.length) return Promise.resolve();
    if (isCartPage()) return refreshCartPage();
    return refreshCartBadge();
  }

  function finishUpdating() {
    recordCountry();
    if (recheck) storageSet(RECHECK_DONE_KEY, '1');
    var root = document.documentElement;
    if (root && root.removeAttribute) root.removeAttribute('data-stale-cart-updating');
  }

  var country = currentCountry();
  var previousCountry = storageGet(COUNTRY_KEY);
  var regionChanged = !!(country && previousCountry && previousCountry !== country);
  var cleanupPage = isCleanupPage();

  // The new country is recorded only once this page's cleanup has finished
  // (or failed), so a shopper who leaves the page mid-cleanup gets the
  // re-check and the message on the next page instead.
  function recordCountry() {
    if (country) storageSet(COUNTRY_KEY, country);
  }

  // Ordinary page without a region switch: nothing to do except remember the
  // country, so a switch made from this page is detected on the next one.
  if (!cleanupPage && !regionChanged) {
    recordCountry();
    return;
  }

  // Re-check after a region switch, and once on the first cleanup page in this
  // browser (the shopper may have switched region before this shipped).
  var recheck = regionChanged || (cleanupPage && !storageGet(RECHECK_DONE_KEY));

  function start() {
    cleanStaleCartLines({ recheck: recheck })
      .then(function (removed) {
        // Announce after a region switch, or when the re-check itself found
        // the item unavailable (e.g. the one-time first re-check).
        if (removed && removed.length && (regionChanged || lastZeroedByRecheck)) {
          announceRemoval(lastRemovedNames);
        }
        return updateDisplay(removed);
      })
      .then(finishUpdating, finishUpdating);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
